import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ComponentCanDeactivate } from '../../../../../models/protocols/component-can-deactivate';
import { EpSelfEnrolmentNavService } from './ep-self-enrolment-nav.service';
import { finalize, map, take } from 'rxjs/operators';
import { EpSelfEnrolmentFlowStepEnum } from './ep-self-enrolment-flow-step.enum';
import { EpSelfEnrolmentFormService } from './ep-self-enrolment-form-service';
import { BehaviorSubject, combineLatest, from } from 'rxjs';
import { TypeService } from '../../../../../services/type/type-service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationOptions } from '../../../../../models/shared/stylesheet/confirmation-options';
import { ConfirmationModalComponent } from '../../../../../modules/confirmation/confirmation-modal/confirmation-modal.component';
import { ModalUtils } from '../../../../../utils/modal-utils';
import { Router } from '@angular/router';
import { Routes } from '../../../../../routes/routes';
import { ToastService } from '../../../../../services/toast-service';
import { CustomError } from '../../../../../models/shared/custom-error';

@Component({
  selector: 'app-ep-self-enrolment',
  templateUrl: './ep-self-enrolment.component.html',
  styleUrls: ['./ep-self-enrolment.component.scss'],
  providers: [EpSelfEnrolmentFormService, EpSelfEnrolmentNavService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EpSelfEnrolmentComponent implements ComponentCanDeactivate, OnInit {
  private formService = inject(EpSelfEnrolmentFormService);
  private navService = inject(EpSelfEnrolmentNavService);
  private ngbModal = inject(NgbModal);
  private router = inject(Router);
  private toastService = inject(ToastService);

  private typeService = inject(TypeService);

  private _isLoading = new BehaviorSubject(false);
  public isLoading$ = this._isLoading.asObservable();

  public currentStep$ = this.navService.currentStep$;
  public currentStepComponent$ = this.navService.currentStepComponent$;

  public showBackToLogin$ = this.currentStep$.pipe(
    map(step => step === EpSelfEnrolmentFlowStepEnum.EmployerInformation)
  );

  public ngOnInit() {
    this.loadInitialData();
  }

  public canDeactivate() {
    if (this.formService.form.pristine) return true;
    if (this.navService.canDeactivate) return true;

    const confirmOpts = new ConfirmationOptions();
    confirmOpts.title = $localize`Are you sure you want to leave?`;
    confirmOpts.bodyText = $localize`Your registration will be discarded.`;
    confirmOpts.continueText = $localize`Continue to Leave`;

    const modalRef = this.ngbModal.open(ConfirmationModalComponent, ModalUtils.defaultModalOptions());
    modalRef.componentInstance.setConfirmationOptions(confirmOpts);
    return from(modalRef.result);
  }

  public goToLogin(): void {
    this.router.navigate([Routes.signIn()]).then();
  }

  private loadInitialData(): void {
    this._isLoading.next(true);
    combineLatest([this.typeService.countryCodes$, this.typeService.provinces$])
      .pipe(
        take(1),
        finalize(() => this._isLoading.next(false))
      )
      .subscribe({
        error: (err: CustomError) => this.toastService.publishError(err)
      });
  }
}
